import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  assigneeGroups,
  resolveActionAssignee,
  UNASSIGNED_LABEL,
  type AssigneeDirectory,
} from './actionEditModel';
import type { ActionEntity } from '../hooks/useScopedActionForm';
import type { ActionAssigneeKind } from '../ICSConstants';

export const ALL_ASSIGNEES = 'all';
export const UNASSIGNED_FILTER = 'unassigned';

export type AssigneeFilterValue = string;

type ActionAssigneeFilterProps = {
  directory: AssigneeDirectory;
  value: AssigneeFilterValue;
  onChange: (next: AssigneeFilterValue) => void;
  /** Limits the groups offered. Omit to show people, teams, and positions. */
  kinds?: readonly ActionAssigneeKind[];
};

function filterLabel(
  value: AssigneeFilterValue,
  groups: ReturnType<typeof assigneeGroups>
): string {
  if (value === ALL_ASSIGNEES) return 'All assignees';
  if (value === UNASSIGNED_FILTER) return UNASSIGNED_LABEL;
  const match = groups
    .flatMap((group) =>
      group.items.map((item) => ({
        token: `${group.kind}:${item.value}`,
        label: item.label,
      }))
    )
    .find((item) => item.token === value);
  return match?.label ?? 'Assignee';
}

/** Optional view filter over the loaded action list, by who owns the task. */
export const ActionAssigneeFilter: React.FC<ActionAssigneeFilterProps> = ({
  directory,
  value,
  onChange,
  kinds,
}) => {
  const groups = useMemo(() => {
    const all = assigneeGroups(directory);
    return kinds ? all.filter((group) => kinds.includes(group.kind)) : all;
  }, [directory, kinds]);

  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(String(next ?? ALL_ASSIGNEES))}
    >
      <SelectTrigger
        size="sm"
        aria-label="Filter by assignee"
        className="tw:w-fit"
        data-tour="hwcg-assignee-filter"
      >
        <SelectValue>{filterLabel(value, groups)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="dark">
        <SelectItem value={ALL_ASSIGNEES}>All assignees</SelectItem>
        <SelectItem value={UNASSIGNED_FILTER}>{UNASSIGNED_LABEL}</SelectItem>
        {groups.map((group) => (
          <SelectGroup key={group.kind}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.items.map((item) => (
              <SelectItem
                key={`${group.kind}:${item.value}`}
                value={`${group.kind}:${item.value}`}
              >
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
};

export function matchesAssigneeFilter(
  action: ActionEntity,
  directory: AssigneeDirectory,
  filter: AssigneeFilterValue
): boolean {
  if (!filter || filter === ALL_ASSIGNEES) return true;
  const resolved = resolveActionAssignee(action, directory);
  if (filter === UNASSIGNED_FILTER) return !resolved.id;
  return `${resolved.kind}:${resolved.id}` === filter;
}

export default ActionAssigneeFilter;
